import * as constants from './constants'
import moment from 'moment'

import get from 'lodash/get'
import omit from 'lodash/omit'

import PageConfig from '../config/page-config'
import createReducer from 'core/reducer-helper'

import PARTNERS from '../../crm/enums/partner-enum'

import { processTableData } from '../modules/utils/table-helper'

moment.updateLocale('en', {
  week: {
    dow: 1
  }
})

export const initialDateRange = {
  start: moment().subtract(29, 'day'),
  end: moment()
}

const CustomDateRangeFormat = 'MMMM Do, YYYY'

const initialState = {
  // all fetched charts containers
  ...PageConfig,

  charts: {},
  filters: {},

  // All date Ranges for each fetched page
  dateRanges: {},
  // Date Range of the current page
  dateRange: {},

  timeStep: {
    id: 0,
    name: 'Day'
  },

  pageKey: null,

  selectedRange: initialDateRange,
  lastFetchedRange: initialDateRange,
  customDate: `${initialDateRange.start.format(CustomDateRangeFormat)} - ${initialDateRange.end.format(
    CustomDateRangeFormat
  )}`,

  // header options
  includeTesters: false,

  selectedBrand: {
    name: 'All Brands'
  },
  latestLogos: [],
  loadingLatestLogos: false,
  latestLogosError: null
}

const AddDateRange = (state, action) => ({
  ...state,
  dateRanges: {
    ...state.dateRanges,
    [action.pageKey]: action.body
  },
  dateRange: action.body
})

const changeDateRange = (state, action) => ({
  ...state,
  selectedRange: {
    start: action.start,
    end: action.end
  },
  customDate: `${action.start ? action.start.format(CustomDateRangeFormat) : '...'} - ${
    action.end ? action.end.format(CustomDateRangeFormat) : '...'
  }`
})

const fetchChartPending = (state, action) => ({
  ...state,
  charts: {
    ...state.charts,
    [`${action.extraArguments.pageKey}${action.extraArguments.chart.name}`]: {
      ...action.extraArguments.chart,
      fetching: true
    }
  }
})

const fetchChartError = (state, action) => ({
  ...state,
  charts: {
    ...state.charts,
    [`${action.extraArguments.pageKey}${action.extraArguments.chart.name}`]: {
      ...action.extraArguments.chart,
      error: action.name,
      dateRange: action.extraArguments.dateRange,
      timeStep: action.extraArguments.timeStep,
      selectedBrand: action.extraArguments.selectedBrand
    }
  }
})

const getHighchartFormat = chart => {
  const series = []
  if (!chart.data) {
    return chart
  }
  if (chart.type === 'table') {
    processTableData(chart)
  }
  if (chart.customDataHandler) {
    chart.data = chart.customDataHandler(chart.data, chart.labels)
  }
  if (chart.data && chart.data instanceof Array) {
    chart.data.forEach(item => {
      if (item.data && item.data instanceof Array) {
        item.data.forEach(serie => {
          // serie.key = serie.key.replace(/([A-Z])/g, ' $1').replace(/(\d+)/g, ' $1')
          // serie.key = startCase(serie.key)
          const currentSerie = series.find(item => {
            return item.name === serie.key
          })
          if (!currentSerie) {
            if (!(chart.hideSeries && chart.hideSeries.includes(serie.key))) {
              serie &&
                series.push({
                  name: serie.key,
                  data: [serie.value],
                  visible: !chart.visible || chart.visible.includes(serie.key)
                })
            }
          } else {
            currentSerie.data.push(serie.value)
          }
        })
      }
    })
  }
  return {
    ...chart,
    data: series.length > 0 ? series : chart.emptyScreenWhenNoData ? [] : chart.data
  }
}

const fetchChartFulfilled = (state, action) => ({
  ...state,
  lastFetchedRange: action.extraArguments.dateRange,
  charts: {
    ...state.charts,
    [`${action.extraArguments.pageKey}${action.extraArguments.chart.name}`]: getHighchartFormat({
      ...action.body,
      ...omit(action.extraArguments.chart, 'data'),
      dateRange: action.extraArguments.dateRange,
      timeStep: action.extraArguments.timeStep,
      includeTesters: action.extraArguments.includeTesters,
      selectedBrand: action.extraArguments.selectedBrand
    })
  }
})

const applyCustomRange = (state, action) => {
  const start = moment(action.start, CustomDateRangeFormat)
  const end = moment(action.end, CustomDateRangeFormat)
  const selectedRange = {
    // if custom date range is lower than min date, select min date
    start:
      moment.duration(start.diff(state.dateRanges[state.pageKey].start)).asHours() > 0
        ? start
        : moment(state.dateRanges[state.pageKey].start),
    end: moment.duration(end.diff(moment())).asHours() < 0 ? end : moment()
  }
  return {
    ...state,
    selectedRange: selectedRange,
    customDate: `${selectedRange.start.format(CustomDateRangeFormat)} - ${selectedRange.end.format(
      CustomDateRangeFormat
    )}`
  }
}

const resetCustomDateRange = state => ({
  ...state,
  selectedRange: state.lastFetchedRange,
  customDate: `${moment(state.lastFetchedRange.start).format(CustomDateRangeFormat)} - ${moment(
    state.lastFetchedRange.end
  ).format(CustomDateRangeFormat)}`
})

const routerLocationChange = (state, action) => {
  const newState = {
    ...state,
    pageKey: Object.keys(PageConfig).find(key => PageConfig[key].route === action.payload.pathname.toLowerCase())
  }

  /*
  When the Gator or Constant Contact brand is selected on one of the Site Report pages
  and the user goes to the Builder Reports, the selectedBrand has to be reset since those
  brands aren't valid there.
  */
  if (
    state.pageKey &&
    state.pageKey.toLowerCase().includes('site') &&
    newState.pageKey &&
    newState.pageKey.toLowerCase().includes('builder') &&
    [PARTNERS.GATOR.id, PARTNERS.CTCTBUILDER.id].includes(get(state.selectedBrand, 'id'))
  ) {
    newState.selectedBrand = { name: 'All Brands' }
  }

  return newState
}

export default createReducer(initialState, {
  [constants.FETCH_DATE_RANGE]: (state, action) => AddDateRange(state, action),
  [constants.DATE_RANGE_FROM_STATE]: (state, action) => ({ ...state, dateRange: action.body }),
  [constants.CHANGE_DATE_RANGE]: (state, action) => changeDateRange(state, action),
  [constants.TIME_STEP_CHANGE]: (state, action) => ({ ...state, timeStep: action.step }),
  [constants.FETCH_CHART_PENDING]: (state, action) => fetchChartPending(state, action),
  [constants.FETCH_CHART_ERROR]: (state, action) => fetchChartError(state, action),
  [constants.FETCH_CHART_FULFILLED]: (state, action) => fetchChartFulfilled(state, action),
  [constants.APPLY_CUSTOM_DATE_RANGE]: (state, action) => applyCustomRange(state, action),
  [constants.RESET_CUSTOM_DATE_RANGE]: state => resetCustomDateRange(state),
  [constants.SET_CUSTOM_DATE]: (state, action) => ({ ...state, customDate: action.value }),
  [constants.ROUTER_LOCATION_CHANGE]: (state, action) => routerLocationChange(state, action),
  [constants.SET_PAGE_KEY]: (state, action) => ({ ...state, pageKey: action.pageKey }),
  [constants.TOGGLE_INCLUDE_TESTERS]: state => ({ ...state, includeTesters: !state.includeTesters }),
  [constants.BRAND_SELECTION]: (state, action) => ({ ...state, selectedBrand: action.value }),
  [constants.GET_LATEST_LOGOS_FULFILLED]: (state, action) => ({
    ...state,
    latestLogos: action.body.data,
    loadingLatestLogos: false
  }),
  [constants.GET_LATEST_LOGOS_PENDING]: (state, action) => ({ ...state, loadingLatestLogos: true }),
  [constants.GET_LATEST_LOGOS_ERROR]: (state, action) => ({
    ...state,
    loadingLatestLogos: false,
    latestLogosError: action.body
  }),
  [constants.SET_TABLE_FILTER]: (state, action) => {
    if (action.reset) {
      return { ...state, filters: {} }
    }
    return { ...state, filters: { ...state.filters, [action.tableName]: action.filter } }
  },
  [constants.SET_TABLE_PAGE]: (state, action) => {
    return { ...state, [`${action.tableName}-page`]: action.page }
  }
})
